/**
 * Demo-reset state, app-wide.
 *
 * The banner lives in the title bar (every page), but the announcement that feeds it arrives
 * on a realtime channel each app opens differently — socket.io on the local node, the
 * per-node events WebSocket in the cloud. This is the seam between them: whoever is holding
 * such a channel publishes with `useSetDemoReset()`, and the title bar reads with
 * `useDemoReset()`.
 *
 * Optional on purpose. `useSetDemoReset()` returns a no-op outside a provider, so a page or
 * hook can publish unconditionally without knowing whether the app it is embedded in mounted
 * one — the same reason nothing here throws the way usePermissions() does.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { DemoResetInfo } from './contracts';

interface DemoResetContextValue {
  info: DemoResetInfo | null;
  set: (info: DemoResetInfo | null) => void;
}

const DemoResetContext = createContext<DemoResetContextValue | null>(null);

export function DemoResetProvider({ children }: { children: ReactNode }) {
  const [info, setInfo] = useState<DemoResetInfo | null>(null);
  // Stable across renders, and it must stay that way: publishers hold it inside long-lived
  // effects (useAlertBoard keeps it in the one that opens the alert socket), so an identity
  // that changed whenever `info` did would tear down and re-open that socket on every
  // heartbeat. A disabled cycle is stored as null, so `info` reads as "there is a wipe
  // coming" and no consumer has to re-check the flag — a node that switches the cycle off
  // announces enabled:false, which is what takes the banner down without a reload.
  const set = useCallback((next: DemoResetInfo | null) => {
    setInfo(next?.enabled ? next : null);
  }, []);
  const value = useMemo<DemoResetContextValue>(() => ({ info, set }), [info, set]);
  return <DemoResetContext.Provider value={value}>{children}</DemoResetContext.Provider>;
}

/** The node's demo wipe cycle, or null — on a normal deployment, always null. */
export function useDemoReset(): DemoResetInfo | null {
  return useContext(DemoResetContext)?.info ?? null;
}

/** Publish an announcement. A no-op when no provider is mounted. */
export function useSetDemoReset(): (info: DemoResetInfo | null) => void {
  const ctx = useContext(DemoResetContext);
  return ctx ? ctx.set : NOOP;
}

const NOOP = () => {};
